import { defineStore } from 'pinia'
import axios from 'axios'

import { useQuerySync } from '@/composables/useQuerySync'
import { extractRouteFilters } from '@/composables/queryFilter'
import { normalizeArrayQueryParam } from '@/composables/queryParams'

export const useProductsStore = defineStore('products', () => {
    const router = useRouter()
    const route = useRoute()

    // ------------------------------------------
    // Init guard
    // ------------------------------------------
    const initialized = ref(false)
    const suppressFetchWatch = ref(true)

    // ------------------------------------------
    // State
    // ------------------------------------------
    const items = ref([])
    const filters = ref([])
    const meta = ref({})
    const context = ref(null)

    const loading = reactive({
        items: false,
        loadMore: false
    })

    const DEFAULT_SORT = '-price'

    const params = reactive({
        q: '',
        startDate: null,
        nights: null,
        adults: 2,
        children: [],
        regionId: '',
        regionGroupId: '',
        locationId: '',
        propertyId: '',
        page: 1,
        filter: {},
        sort: DEFAULT_SORT
    })

    const routeFilters = ref({})

    // ------------------------------------------
    // SEO catalog path (/nastanitve/:regionGroup[/:region[/:location[/:property]]])
    // ------------------------------------------
    const pathSegments = computed(() => {
        const { regionGroup, region, location, property: propertySlug } = route.params
        if (!regionGroup) return null
        return [regionGroup, region, location, propertySlug].filter(Boolean)
    })

    // ------------------------------------------
    // Helpers
    // ------------------------------------------
    const enableResetFilter = computed(() => Object.values(params.filter).some((v) => (Array.isArray(v) ? v.length : !!v)))

    const resetFilters = () => {
        Object.keys(params.filter).forEach((k) => {
            params.filter[k] = Array.isArray(params.filter[k]) ? [] : null
        })
        params.page = 1
    }

    // ------------------------------------------
    // URL sync (after init only)
    // ------------------------------------------

    const urlQuery = computed(() => ({
        sort: params.sort !== DEFAULT_SORT ? params.sort : undefined,
        filter: params.filter
    }))

    useQuerySync(urlQuery, {
        keys: ['sort', 'filter'],
        debounce: 300
    })

    // ------------------------------------------
    // API
    // ------------------------------------------
    const fetchData = async (mode = 'replace') => {
        try {
            items.value = mode === 'replace' ? [] : items.value
            loading.items = mode === 'replace'
            loading.loadMore = mode === 'append'

            const url = pathSegments.value ? '/api/catalog/' + pathSegments.value.map(encodeURIComponent).join('/') : '/api/products'
            const { data } = await axios.get(url, { params })

            if (data.redirect) {
                await router.replace({ path: '/nastanitve/' + data.redirect, query: route.query })
                return
            }

            const list = data.data ?? []

            if (mode === 'append') {
                items.value.push(...list)
            } else {
                items.value = list
            }

            meta.value = data.meta ?? {}
            filters.value = data.filters ?? []
            context.value = data.context ?? null

            if (!Object.keys(params.filter).length && filters.value.length) {
                params.filter = Object.fromEntries(filters.value.map((f) => [f.name, f.type === 'multiselect' ? [] : null]))
                params.filter = { ...params.filter, ...routeFilters.value }
            }
        } finally {
            loading.items = false
            loading.loadMore = false
        }
    }

    // ------------------------------------------
    // Watchers
    // ------------------------------------------
    watch(
        () => [params.filter, params.sort],
        () => {
            if (!initialized.value || suppressFetchWatch.value) return
            params.page = 1
            fetchData('replace')
        },
        { deep: true }
    )

    watch(
        () => params.page,
        (p, prev) => {
            if (!initialized.value) return
            if (p > prev) fetchData('append')
        }
    )

    // ------------------------------------------
    // Init
    // ------------------------------------------
    const initialize = async () => {
        await router.isReady()

        suppressFetchWatch.value = true

        params.startDate = route.query.start_date ?? null
        params.nights = route.query.nights ?? null
        params.adults = Number(route.query.adults ?? 2)
        params.children = normalizeArrayQueryParam(route.query.children)
        params.page = Number(route.query.page ?? 1)
        params.sort = route.query.sort ?? '-is_featured'
        routeFilters.value = extractRouteFilters(route.query)
        params.filter = { ...routeFilters.value }

        // Camp/location filters: implicit in the URL path for the SEO
        // listing route, otherwise read from query params as before.
        if (pathSegments.value) {
            params.q = ''
            params.regionId = ''
            params.regionGroupId = ''
            params.locationId = ''
            params.propertyId = ''
        } else {
            params.q = route.query.q ?? ''
            params.regionId = route.query.region_id ?? ''
            params.regionGroupId = route.query.region_group_id ?? ''
            params.locationId = route.query.location_id ?? ''
            params.propertyId = route.query.property_id ?? ''
        }

        await fetchData('replace')

        // Populate the "Where?" field from the resolved context on the SEO
        // listing route: otherwise it renders blank (the location is only
        // implicit in the URL path), and re-submitting the search form
        // (e.g. after just changing dates) would silently drop the location
        // by falling back to a plain /ponudbe search with no geo filter.
        if (pathSegments.value && context.value) {
            params.q = context.value.label ?? ''

            const depth = pathSegments.value.length
            if (depth === 1) params.regionGroupId = context.value.id
            if (depth === 2) params.regionId = context.value.id
            if (depth === 3) params.locationId = context.value.id
            if (depth === 4) params.propertyId = context.value.id
        }

        // Fill missing filter keys after first response, but keep URL-provided values.
        if (filters.value.length) {
            const defaults = Object.fromEntries(filters.value.map((f) => [f.name, f.type === 'multiselect' ? [] : null]))
            params.filter = { ...defaults, ...params.filter }
        }

        initialized.value = true
        await nextTick()
        suppressFetchWatch.value = false
    }

    // ------------------------------------------
    // Expose
    // ------------------------------------------
    return {
        params,
        items,
        filters,
        meta,
        context,
        loading,

        lastPage: computed(() => meta.value.lastPage ?? 1),
        total: computed(() => meta.value.total ?? 0),
        enableResetFilter,

        initialize,
        loadMore: () => {
            if (params.page < (meta.value.lastPage ?? 1)) {
                params.page++
            }
        },
        resetFilters
    }
})
